import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  CreditCard,
  Shield,
  CheckCircle2,
  Leaf,
  Sparkles,
  UserCheck,
  Video,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const PLAN_DETAILS = {
  basic: {
    name: 'AI Ayurvedic Assessment',
    price: 1.49,
    originalPrice: 28,
    icon: Sparkles,
    disclaimer: 'Not a comprehensive diagnosis or a substitute for professional healthcare advice.',
    features: [
      'Customised Report',
      'Modern wellness interpretation',
      'Lifestyle recommendations',
      'Herbal remedy suggestions',
      'Downloadable PDF report'
    ]
  },
  verified: {
    name: 'Doctor-Reviewed Assessment',
    price: 37,
    originalPrice: 74,
    icon: UserCheck,
    disclaimer: 'Non-clinical review. Does not replace medical advice.',
    features: [
      'Everything in AI Assessment',
      'Review by certified Vaidya',
      'Personalized doctor notes',
      'Priority support',
      'Verified stamp on report'
    ]
  },
  consultation: {
    name: '1-on-1 Ayurvedic Consultation',
    price: 45,
    originalPrice: 90,
    icon: Video,
    disclaimer: 'Ayurvedic diet and lifestyle guidance only.',
    features: [
      'Everything in Doctor-Reviewed',
      '45-minute video consultation',
      'Ask questions directly',
      'Follow-up guidance',
      'Personalized wellness plan'
    ]
  }
};

export const Payment = () => {
  const navigate = useNavigate();
  const { id: consultationId } = useParams();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchConsultation();
  }, [consultationId]);

  const fetchConsultation = async () => {
    try {
      const response = await api.get(`/api/consultations/${consultationId}`);
      setConsultation(response.data);
      
      // If already paid, redirect to questions
      if (response.data.payment_status === 'paid') {
        navigate(`/consultation/${consultationId}/questions`);
      }
    } catch (error) {
      console.error('Error fetching consultation:', error);
      toast.error('Failed to load consultation');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post(
        '/api/payments/create-checkout',
        { 
          consultation_id: consultationId,
          origin_url: originUrl
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const plan = consultation?.plan || 'basic';
  const planDetails = PLAN_DETAILS[plan] || PLAN_DETAILS.basic;
  const PlanIcon = planDetails.icon;

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="payment-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-stone-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Complete Payment
            </h1>
            <p className="text-sm text-stone-500">Step 2 of 4: Secure checkout</p>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center gap-2 mb-8">
          {['Concerns', 'Payment', 'Questions', 'Assessment'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className={`step-indicator ${index < 1 ? 'completed' : index === 1 ? 'active' : 'pending'}`}>
                {index < 1 ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
              </div>
              <span className={`ml-2 text-sm ${index === 1 ? 'text-[hsl(22,70%,44%)] font-medium' : index < 1 ? 'text-[hsl(135,15%,33%)]' : 'text-stone-400'}`}>
                {step}
              </span>
              {index < 3 && <div className={`w-8 h-px mx-4 ${index < 1 ? 'bg-[hsl(135,15%,33%)]' : 'bg-stone-300'}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 pb-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="consultation-card">
            <h2 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Order Summary
            </h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-4 pb-4 border-b border-stone-200">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  plan === 'consultation' ? 'bg-purple-100 text-purple-600' :
                  plan === 'verified' ? 'bg-blue-100 text-blue-600' :
                  'bg-[hsl(22,70%,44%)/0.1] text-[hsl(22,70%,44%)]'
                }`}>
                  <PlanIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[hsl(120,10%,18%)]">{planDetails.name}</h3>
                  <p className="text-sm text-stone-500">
                    Personalized Ayurvedic consultation
                  </p>
                </div>
                <span className="font-semibold text-[hsl(120,10%,18%)]">${planDetails.price}.00 USD</span>
              </div>
            </div>

            {/* Patient Summary */}
            <div className="bg-stone-50 rounded-xl p-4 mb-6">
              <h4 className="text-sm font-semibold text-stone-700 mb-3">Your Information Summary:</h4>
              
              {/* Patient Details */}
              <div className="grid grid-cols-2 gap-2 text-sm mb-3 pb-3 border-b border-stone-200">
                <div>
                  <span className="text-stone-500">Name:</span>
                  <span className="ml-2 text-stone-700 font-medium">{consultation?.client_name}</span>
                </div>
                <div>
                  <span className="text-stone-500">Email:</span>
                  <span className="ml-2 text-stone-700 font-medium">{consultation?.client_email}</span>
                </div>
                <div>
                  <span className="text-stone-500">Phone:</span>
                  <span className="ml-2 text-stone-700 font-medium">{consultation?.client_phone}</span>
                </div>
                {consultation?.client_gender && (
                  <div>
                    <span className="text-stone-500">Gender:</span>
                    <span className="ml-2 text-stone-700 font-medium capitalize">{consultation.client_gender.replace('_', ' ')}</span>
                  </div>
                )}
                {consultation?.client_age > 0 && (
                  <div>
                    <span className="text-stone-500">Age:</span>
                    <span className="ml-2 text-stone-700 font-medium">{consultation.client_age} years</span>
                  </div>
                )}
              </div>
              
              {/* Health Concerns */}
              <div className="mb-3 pb-3 border-b border-stone-200">
                <h5 className="text-xs font-medium text-stone-500 mb-2">Health & Lifestyle Concerns:</h5>
                <div className="flex flex-wrap gap-2">
                  {consultation?.symptoms?.filter(s => s.name !== 'Detailed Description').map((symptom, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-[hsl(22,70%,44%)/0.1] text-[hsl(22,70%,44%)] rounded-full text-xs font-medium"
                    >
                      {symptom.name}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Detailed Description */}
              {consultation?.detailed_description && (
                <div>
                  <h5 className="text-xs font-medium text-stone-500 mb-2">Detailed Description:</h5>
                  <p className="text-sm text-stone-600 bg-white rounded-lg p-3 border border-stone-200">
                    {consultation.detailed_description}
                  </p>
                </div>
              )}
            </div>

            {/* What's Included */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700">What&apos;s Included:</h4>
              {planDetails.features.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-stone-600">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(135,15%,33%)]" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Payment Section */}
          <div className="consultation-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[hsl(22,70%,44%)/0.1] rounded-2xl flex items-center justify-center">
                <CreditCard className="w-7 h-7 text-[hsl(22,70%,44%)]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Secure Payment
                </h2>
                <p className="text-stone-600 text-sm">Powered by Stripe</p>
              </div>
            </div>

            <div className="bg-[hsl(43,70%,52%)/0.1] rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-stone-700">Total Amount</span>
                <span className="text-2xl font-bold text-[hsl(22,70%,44%)]">${planDetails.price}.00 <span className="text-sm font-normal">USD</span></span>
              </div>
              <p className="text-sm text-stone-500 mt-1">One-time payment, no subscription</p>
            </div>

            <Button
              onClick={handlePayment}
              disabled={processing}
              className="w-full btn-primary text-lg py-6 mb-4"
              data-testid="pay-now-btn"
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pay Now - ${planDetails.price}.00
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
              <Shield className="w-4 h-4" />
              <span>Secure, encrypted payment</span>
            </div>

            {/* Trust Badges */}
            <div className="mt-6 pt-6 border-t border-stone-200">
              <div className="flex items-center justify-center gap-6 text-stone-400">
                <span className="text-xs">256-bit SSL</span>
                <span className="text-xs">PCI Compliant</span>
                <span className="text-xs">Money-back Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
